import {
  keepPreviousData,
  useQuery,
  type UseQueryResult,
} from "@tanstack/react-query";
import type { AcquisitionSourceFilter } from "../acquisition/acquisitionSourceFilter";

/** The date-range inputs shared by every user-analytics query. */
type UserAnalyticsDateRange = AcquisitionSourceFilter & {
  /** Inclusive start of the reporting window in YYYY-MM-DD. */
  start_date: string;
  /** Inclusive end of the reporting window in YYYY-MM-DD. */
  end_date: string;
};

/**
 * Shared `useQuery` wrapper for the user-analytics hooks. Every hook keys its
 * cache on the endpoint, date range, and Source selection, stays disabled
 * until both dates are present, and mirrors the same staleness / focus
 * behaviour. Centralising the options block keeps those invariants identical.
 */
export function useUserAnalyticsDateRangeQuery<T>(
  queryKey: string,
  params: UserAnalyticsDateRange,
  queryFn: () => Promise<T>,
): UseQueryResult<T> {
  const { start_date, end_date, source } = params;

  return useQuery<T>({
    queryKey: [queryKey, start_date, end_date, source?.join(",")],
    queryFn,
    enabled: Boolean(start_date && end_date),
    placeholderData: keepPreviousData,
    staleTime: 1000 * 60,
    refetchOnWindowFocus: false,
  });
}
